import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import closeIcon from '@assets/filtermodalicons/filtermodalCrossIcon.svg';
import MultiSelectDropdown from '@/components/common/MultiSelectDrodpdown/MultiSelectDrodpdown';
import Button from '@/components/common/Button/Button';
import './FilterPanel.css';

const FilterPanel = ({ 
  isOpen, 
  onClose, 
  onApply, 
  filters, 
  triggerRef,
  filterConfig = []
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [panelPosition, setPanelPosition] = useState({});
  const panelRef = useRef(null);

  // Sync local filters when panel opens or filters prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  // Calculate panel position based on trigger button on mobile
  useEffect(() => {
    if (!isOpen || !triggerRef?.current || !panelRef.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // On mobile, position the panel below the trigger button using fixed positioning
        setPanelPosition({
          top: `${triggerRect.bottom + 8}px`,
          right: '16px',
          left: 'auto',
          bottom: 'auto'
        });
      } else {
        // On desktop, clear inline styles to use CSS positioning (absolute)
        setPanelPosition({});
      }
    };

    updatePosition();
    
    // Only add listeners on mobile
    if (window.innerWidth <= 768) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', onClose, true); // close on scroll

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', onClose, true);
      };
    }
  }, [isOpen, triggerRef]);

  // Handle click outside to close panel
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking inside the panel
      if (panelRef.current && panelRef.current.contains(event.target)) {
        return;
      }
      
      // Don't close if clicking on the trigger button
      if (triggerRef?.current && triggerRef.current.contains(event.target)) {
        return;
      }
      
      // Close if clicking outside
      onClose();
    };

    // Add listener with a small delay
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Handle multiselect filter changes
  const handleMultiSelectChange = (filterKey, values) => {
    setLocalFilters(prev => ({ ...prev, [filterKey]: values }));
  };

  // Handle radio pill filter changes (single select)
  const handleRadioPillChange = (filterKey, value) => {
    setLocalFilters(prev => {
      const currentValue = prev[filterKey];
      // Toggle: if clicking the same value, deselect it; otherwise select the new value
      const newValue = currentValue === value ? '' : value;
      return { ...prev, [filterKey]: newValue };
    });
  };

  // Handle pill filter changes
  const handlePillChange = (filterKey, value) => {
    setLocalFilters(prev => {
      const currentValues = prev[filterKey] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterKey]: newValues };
    });
  };

  // Clear specific filter
  const handleClear = (filterKey) => {
    setLocalFilters(prev => ({ ...prev, [filterKey]: [] }));
  };

  // Reset all filters
  const handleReset = () => {
    const resetFilters = {};
    filterConfig.forEach(config => {
      if (config.type === 'radio-pills') {
        resetFilters[config.key] = '';
      } else {
        resetFilters[config.key] = [];
      }
    });
    setLocalFilters(resetFilters);
    onApply(resetFilters); // Apply the reset filters immediately
    onClose(); // Close the filter panel
  };

  // Apply filters
  const handleApply = () => {
    onApply(localFilters);
    onClose(); // Close the filter panel
  };

  if (!isOpen) return null;

  return (
    <div className="filter-panel" ref={panelRef} style={panelPosition}>
      {/* Header */}
      <div className="filter-panel__header">
        <h3 className="filter-panel__title">Filters</h3>
        <Button variant="link" className="filter-panel__close" onClick={onClose}>
          <img src={closeIcon} alt="Close" className="filter-panel__close-icon" />
        </Button>
      </div>

      {/* Body */}
      <div className="filter-panel__body">
        {filterConfig.map((config) => {
          if (config.type === 'multiselect') {
            const optionsFormatted = config.options.map(opt => 
              typeof opt === 'object' && opt !== null
                ? { value: String(opt.value), label: String(opt.label) }
                : { value: opt, label: opt }
            );

            return (
              <div key={config.key} className="filter-section">
                <div className="filter-section__header">
                  <span className="filter-section__title">{config.label}</span>
                  <button className="filter-section__clear" onClick={() => handleClear(config.key)}>
                    Clear
                  </button>
                </div>
                <MultiSelectDropdown
                  options={optionsFormatted}
                  values={localFilters[config.key] || []}
                  onChange={(values) => handleMultiSelectChange(config.key, values)}
                  placeholder={config.placeholder || `Select ${config.label}`}
                  className="filter-dropdown"
                />
              </div>
            );
          }

          if (config.type === 'radio-pills') {
            return (
              <div key={config.key} className="filter-section">
                <div className="filter-section__header">
                  <span className="filter-section__title">{config.label}</span>
                  <button className="filter-section__clear" onClick={() => handleClear(config.key)}>
                    Clear
                  </button>
                </div>
                <div className="filter-section__pills">
                  {config.options.map(option => (
                    <button
                      key={option}
                      className={`filter-pill ${localFilters[config.key] === option ? 'filter-pill--active' : ''}`}
                      onClick={() => handleRadioPillChange(config.key, option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          if (config.type === 'pills') {
            return (
              <div key={config.key} className="filter-section">
                <div className="filter-section__header">
                  <span className="filter-section__title">{config.label}</span>
                  <button className="filter-section__clear" onClick={() => handleClear(config.key)}>
                    Clear
                  </button>
                </div>
                <div className="filter-section__pills">
                  {config.options.map(option => (
                    <button
                      key={option}
                      className={`filter-pill ${localFilters[config.key]?.includes(option) ? 'filter-pill--active' : ''}`}
                      onClick={() => handlePillChange(config.key, option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>

      {/* Footer */}
      <div className="filter-panel__footer">
        <Button 
          variant="secondary" 
          className="filter-btn filter-btn--reset" 
          onClick={handleReset}
          onMouseDown={(e) => e.stopPropagation()}
        >
          Reset
        </Button>
        <Button 
          variant="primary" 
          className="filter-btn filter-btn--apply" 
          onClick={handleApply}
          onMouseDown={(e) => e.stopPropagation()}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

FilterPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  triggerRef: PropTypes.object,
  filterConfig: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['multiselect', 'pills', 'radio-pills']).isRequired,
    options: PropTypes.array.isRequired,
    placeholder: PropTypes.string,
  })),
};

export default FilterPanel;
